import mixpanel from "mixpanel-browser";

// Check if Mixpanel is properly initialized
const isMixpanelReady = () => {
  try {
    return mixpanel && typeof mixpanel.track === "function";
  } catch (_error) {
    return false;
  }
};

// Check if we're in development mode
const isDevelopment = () => {
  return (
    import.meta.env.MODE === "development" ||
    import.meta.env.DEV === true ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
};

// Enhanced logging for development
const logMixpanelEvent = (
  eventName: string,
  properties: any,
  isSuccess: boolean = true,
) => {
  if (isDevelopment()) {
    const status = isSuccess ? "✅" : "❌";
    console.group(`${status} Mixpanel Event: ${eventName}`);
    console.log("Properties:", properties);
    console.log("Timestamp:", new Date().toISOString());
    console.log("User ID:", properties.user_id);
    console.groupEnd();
  }
};

// Initialize Mixpanel (this should already be done in index.tsx)
// mixpanel.init(MIXPANEL_TOKEN, { autocapture: true });


/**
 * Track calendar connection attempt
 * @param provider - The calendar provider being attempted
 * @param userId - The user ID attempting the connection
 */
export const trackCalendarConnectionAttempt = (
  provider: string,
  userId: string,
) => {
  if (!isMixpanelReady()) {
    console.warn("Mixpanel not ready, skipping connection attempt tracking");
    return;
  }

  try {
    mixpanel.identify(userId);

    mixpanel.track("Calendar Connection Attempt", {
      provider: provider.toLowerCase(),
      user_id: userId,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error(
      "Failed to track calendar connection attempt in Mixpanel:",
      error,
    );
  }
};

/**
 * Track calendar connection failure
 * @param provider - The calendar provider that failed
 * @param userId - The user ID who experienced the failure
 * @param error - The error message or code
 */
export const trackCalendarConnectionFailure = (
  provider: string,
  userId: string,
  error: string,
) => {
  if (!isMixpanelReady()) {
    console.warn("Mixpanel not ready, skipping connection failure tracking");
    return;
  }

  try {
    mixpanel.identify(userId);

    mixpanel.track("Calendar Connection Failed", {
      provider: provider.toLowerCase(),
      user_id: userId,
      error: error,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error(
      "Failed to track calendar connection failure in Mixpanel:",
      error,
    );
  }
};


/**
 * Set user properties in Mixpanel
 * @param userId - The user ID
 * @param properties - User properties to set
 */
export const setMixpanelUserProperties = (
  userId: string,
  properties: Record<string, any> = {},
) => {
  if (!isMixpanelReady()) {
    console.warn("Mixpanel not ready, skipping user properties setting");
    return;
  }

  try {
    mixpanel.identify(userId);

    mixpanel.people.set({
      user_id: userId,
      ...properties,
    });
  } catch (error) {
    console.error("Failed to set Mixpanel user properties:", error);
  }
};

/**
 * Track user login in Mixpanel
 * @param userId - The user ID who logged in
 * @param method - The login method (google, email, etc.)
 * @param additionalProps - Any additional properties to track
 */
export const trackUserLogin = (
  userId: string,
  method: string,
  additionalProps: Record<string, any> = {},
) => {
  if (!isMixpanelReady()) {
    console.warn("Mixpanel not ready, skipping login tracking");
    return;
  }

  try {
    // Set user identity for this event
    mixpanel.identify(userId);

    const eventProperties = {
      method: method.toLowerCase(),
      user_id: userId,
      timestamp: new Date().toISOString(),
      ...additionalProps,
    };

    // Track the login event
    mixpanel.track("User Logged In", eventProperties);

    // Set user properties for future events
    mixpanel.people.set({
      $email: localStorage.getItem("userEmail") || undefined,
      $name: localStorage.getItem("userName") || undefined,
      last_login: new Date().toISOString(),
      login_method: method.toLowerCase(),
    });

    // Enhanced logging for development
    logMixpanelEvent("User Logged In", eventProperties);
  } catch (error) {
    console.error("Failed to track user login in Mixpanel:", error);
  }
};


/**
 * Track login attempt in Mixpanel
 * @param method - The login method being attempted
 * @param additionalProps - Any additional properties to track
 */
export const trackLoginAttempt = (
  method: string,
  additionalProps: Record<string, any> = {},
) => {
  if (!isMixpanelReady()) {
    console.warn("Mixpanel not ready, skipping login attempt tracking");
    return;
  }

  try {
    const eventProperties = {
      method: method.toLowerCase(),
      timestamp: new Date().toISOString(),
      ...additionalProps,
    };

    // Track the login attempt event
    mixpanel.track("Login Attempt", eventProperties);

    // Enhanced logging for development
    logMixpanelEvent("Login Attempt", eventProperties);
  } catch (error) {
    console.error("Failed to track login attempt in Mixpanel:", error);
  }
};

/**
 * Track login failure in Mixpanel
 * @param method - The login method that failed
 * @param error - The error message or code
 * @param additionalProps - Any additional properties to track
 */
export const trackLoginFailure = (
  method: string,
  error: string,
  additionalProps: Record<string, any> = {},
) => {
  if (!isMixpanelReady()) {
    console.warn("Mixpanel not ready, skipping login failure tracking");
    return;
  }

  try {
    const eventProperties = {
      method: method.toLowerCase(),
      error: error,
      timestamp: new Date().toISOString(),
      ...additionalProps,
    };

    // Track the login failure event
    mixpanel.track("Login Failed", eventProperties);

    // Enhanced logging for development
    logMixpanelEvent("Login Failed", eventProperties, false);
  } catch (error) {
    console.error("Failed to track login failure in Mixpanel:", error);
  }
};

/**
 * Test function for local development - call this from browser console
 * Usage: window.testMixpanel('test_user_123')
 */
export const testMixpanel = (testUserId: string = "test_user_123") => {
  if (!isMixpanelReady()) {
    console.error("❌ Mixpanel not ready");
    return;
  }

  try {
    console.group("🧪 Testing Mixpanel Integration");

    // Test user identification
    mixpanel.identify(testUserId);
    console.log("✅ User identified:", testUserId);

    // Test event tracking
    const testEvent = {
      provider: "test",
      user_id: testUserId,
      timestamp: new Date().toISOString(),
      test_mode: true,
    };

    mixpanel.track("Test Event", testEvent);
    console.log("✅ Test event tracked:", testEvent);

    // Test user properties
    mixpanel.people.set({
      user_id: testUserId,
      test_user: true,
      test_timestamp: new Date().toISOString(),
    });
    console.log("✅ User properties set");

    console.log("🎉 Mixpanel test completed successfully!");
    console.log(
      "📊 Check your Mixpanel dashboard Live View to see the test event",
    );
    console.groupEnd();
  } catch (error) {
    console.error("❌ Mixpanel test failed:", error);
    console.groupEnd();
  }
};

// Make test function available globally in development
if (typeof window !== "undefined" && isDevelopment()) {
  (window as any).testMixpanel = testMixpanel;
  console.log("🧪 Mixpanel test function available: window.testMixpanel()");
}
